import React, { useState, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { aiFetch } from '../utils/aiFetch';

// Hash layout state — used to skip re-analysis when nothing changed
const hashLayout = (shop, placed, doors, drains, form) => {
  const compact = {
    s: [shop.w, shop.h, shop.unit],
    p: placed.map(p => [p.id, p.cx, p.cy, p.fw * p.scaleX, p.fh * p.scaleY, p.rot, p.label, p.isRoom]),
    d: doors.map(d => [d.type, d.x, d.y, d.w, d.rot]),
    fd: drains.map(d => [d.cx, d.cy, d.w, d.h]),
    f: [form.ceilingHeight, form.concreteThickness, form.concreteCondition],
  };
  return JSON.stringify(compact);
};

// Pre-compute distances + clearances so Claude reasons on accurate numbers, not estimates
const buildAnalysisPayload = (shop, placed, doors, drains, form) => {
  const equipment = placed.filter(p => !p.isRoom);
  const rooms = placed.filter(p => p.isRoom);

  // Helper: rotated bounding box (defined here so pair calc can use it before wallClear)
  const getRotatedBounds = (e) => {
    const w = e.fw * e.scaleX;
    const h = e.fh * e.scaleY;
    const rad = (e.rot || 0) * Math.PI / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    const boundW = w * cos + h * sin;
    const boundH = w * sin + h * cos;
    return { hw: boundW / 2, hh: boundH / 2, w, h };
  };

  // Compute clearance between every equipment pair (edge-to-edge distance)
  // Uses rotated bounding boxes so rotated lifts/equipment measure correctly
  const pairs = [];
  for (let i = 0; i < equipment.length; i++) {
    for (let j = i + 1; j < equipment.length; j++) {
      const a = equipment[i], b = equipment[j];
      const ab = getRotatedBounds(a);
      const bb = getRotatedBounds(b);
      const dx = Math.max(0, Math.abs(a.cx - b.cx) - ab.hw - bb.hw);
      const dy = Math.max(0, Math.abs(a.cy - b.cy) - ab.hh - bb.hh);
      const clearance = Math.sqrt(dx * dx + dy * dy);
      pairs.push({
        a: a.label || a.name,
        b: b.label || b.name,
        clearance_ft: +clearance.toFixed(1),
      });
    }
  }

  // Wall clearances per equipment item (distance from each edge to shop boundary)
  // Now correctly handles rotated equipment by using the rotated bounding box
  const wallClear = equipment.map(e => {
    const { hw, hh, w, h } = getRotatedBounds(e);
    return {
      item: e.label || e.name,
      type: e.id,
      footprint_ft: `${w.toFixed(1)} x ${h.toFixed(1)}`,
      rotation_deg: Math.round(e.rot),
      requirements: e.requirements || [],
      capacity: e.capacity || null,
      from_west_ft: +(e.cx - hw).toFixed(1),
      from_east_ft: +(shop.w - (e.cx + hw)).toFixed(1),
      from_north_ft: +(e.cy - hh).toFixed(1),
      from_south_ft: +(shop.h - (e.cy + hh)).toFixed(1),
    };
  });

  // Drains within or near equipment footprints (potential conflicts)
  const drainConflicts = drains.map(d => {
    const inside = equipment.filter(e => {
      const hw = e.fw * e.scaleX / 2;
      const hh = e.fh * e.scaleY / 2;
      return d.cx >= e.cx - hw && d.cx <= e.cx + hw &&
             d.cy >= e.cy - hh && d.cy <= e.cy + hh;
    });
    return {
      drain_at: `${d.cx.toFixed(1)}, ${d.cy.toFixed(1)}`,
      label: d.label,
      under_equipment: inside.map(e => e.label || e.name),
    };
  });

  return {
    shop: { width_ft: shop.w, depth_ft: shop.h, unit: shop.unit },
    site: {
      ceiling_height: form.ceilingHeight || 'unspecified',
      concrete_thickness: form.concreteThickness || 'unspecified',
      concrete_condition: form.concreteCondition || 'unspecified',
    },
    equipment_count: equipment.length,
    rooms: rooms.map(r => ({
      name: r.label,
      footprint_ft: `${(r.fw * r.scaleX).toFixed(1)} x ${(r.fh * r.scaleY).toFixed(1)}`,
    })),
    doors: doors.map(d => ({ type: d.type, side: d.side || 'unknown', width_ft: d.w })),
    equipment: wallClear,
    pair_clearances: pairs,
    floor_drains: drainConflicts,
  };
};

export default function LayoutAnalyzer({ shop, placed, doors, drains, form, onClose }) {
  const { getToken } = useAuth(); // Clerk session token for AI API calls
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [cache, setCache] = useState({}); // memoize per-hash results

  const layoutHash = useMemo(
    () => hashLayout(shop, placed, doors, drains, form),
    [shop, placed, doors, drains, form]
  );

  const runAnalysis = async () => {
    // Cache hit — skip API call entirely
    if (cache[layoutHash]) {
      setResult(cache[layoutHash]);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = buildAnalysisPayload(shop, placed, doors, drains, form);

      // Prompt asks for strict JSON output so we can render structured cards
      const systemPrompt = `You are ShopBuilder's layout review system — an expert automotive shop layout analyzer with deep knowledge of real-world shop installations. You review pre-computed layout data and identify ONLY genuine issues that would prevent the shop from functioning properly. Never refer to yourself as Claude, an AI, or an assistant — you are ShopBuilder's analysis engine.

DATA RULES:
- All distances are PRE-COMPUTED and accurate. DO NOT estimate or recalculate.
- Pair clearances are edge-to-edge in feet.
- Wall clearances are from each side of equipment to shop boundary.

REAL-WORLD CLEARANCE STANDARDS (be lenient — match actual shop floor practice):
- 2-post lifts: 18 inches (1.5 ft) MINIMUM side clearance is acceptable. Many shops run tighter. Do NOT warn about clearances 1.5 ft or greater.
- 2-post lifts CAN BE STAGGERED (offset front-to-back) — this is common, intentional, and allows tighter overall spacing. Never flag staggered or asymmetric lift arrangements as a problem.
- Mixed orientations (lifts rotated different directions) are normal in real shops. Do not flag.
- 4-post lifts: need ~24 ft of bay depth for full-size trucks. Side clearance of 2 ft is fine.
- Mobile column lifts: spacing varies by vehicle wheelbase. Don't flag unless columns overlap.
- Tire equipment (changer/balancer): pairing is a CONVENIENCE, never a requirement. Do not warn if separated.
- Air compressors: outdoor/wall placement is a preference, never a critical issue.
- Workbenches, tool cabinets: flexible placement, no clearance rules.

WHAT TO ACTUALLY FLAG:
CRITICAL (rare — only true blockers):
  - Equipment physically overlapping or extending past shop walls
  - Ceiling height incompatible with lift type (2-post needs 11 ft, 4-post needs 12 ft)
  - Floor drain UNDER a lift footprint (anchor bolt conflict — real installation problem)
  - Equipment placed on top of or inside a room

WARNING (only obvious workflow blockers):
  - Lift placed where no vehicle could reach it (no clear path from any door to lift)
  - Overhead door blocked by equipment within 5 ft of door swing
  - Lift placed less than 1.5 ft from another lift (genuinely too tight)
  - Equipment with stated requirement (e.g. "Drainage") placed far from any drain

INFO (helpful suggestions only — keep these brief and optional):
  - Air compressor located far from outside wall (ventilation suggestion)
  - Tire changer and balancer separated (workflow optimization)
  - Lift far from any door (consider vehicle access path)

DO NOT FLAG:
- Side clearances 1.5 ft or greater between lifts
- Staggered, offset, or asymmetric lift arrangements
- Mixed equipment orientations  
- Tire equipment not paired
- Air compressor not on outside wall (unless ventilation is explicitly poor)
- Aesthetic or "balance" concerns
- Anything based on assumption rather than measured data

Respond ONLY with valid JSON (no markdown, no preamble) in this exact shape:
{
  "summary": "1-2 sentence overall assessment. If layout is good, say so plainly.",
  "issues": [
    {
      "severity": "critical" | "warning" | "info",
      "category": "Clearance" | "Safety" | "Workflow" | "Code" | "Layout",
      "title": "Short issue name (max 60 chars)",
      "description": "Specific issue with measurement reference (max 200 chars)",
      "affected": ["item names involved"]
    }
  ]
}

OUTPUT RULES:
- Maximum 6 issues total. Prefer fewer.
- A clean layout with zero issues is GOOD — return an empty issues array and a positive summary.
- Cite actual measurements from the data when flagging.
- Never invent issues to fill space. If nothing is wrong, say nothing.`;

      const userPrompt = `Analyze this automotive shop layout:\n\n${JSON.stringify(payload, null, 2)}`;

      // aiFetch attaches Clerk auth token and handles 401/403 with friendly errors
      const data = await aiFetch({
        model: 'claude-sonnet-5',
        max_tokens: 2000,
        system: systemPrompt,
        messages: [{ role: 'user', content: userPrompt }],
      }, getToken);

      const rawText = data.content?.[0]?.text || data.text || '';

      // Strip any accidental markdown code fences before parsing
      const cleanText = rawText.replace(/```json\s*|\s*```/g, '').trim();
      const parsed = JSON.parse(cleanText);

      // Cache result so re-clicks on same layout don't re-bill
      setCache(c => ({ ...c, [layoutHash]: parsed }));
      setResult(parsed);
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Severity styling map for visual distinction
  const sevStyles = {
    critical: { color: '#ff4444', bg: 'rgba(255,68,68,.08)', border: '#ff444444', label: 'CRITICAL' },
    warning: { color: '#ffaa44', bg: 'rgba(255,170,68,.08)', border: '#ffaa4444', label: 'WARNING' },
    info: { color: '#44aaff', bg: 'rgba(68,170,255,.08)', border: '#44aaff44', label: 'SUGGESTION' },
  };

  // Sort issues by severity for visual hierarchy
  const sortedIssues = result?.issues?.slice().sort((a, b) => {
    const order = { critical: 0, warning: 1, info: 2 };
    return (order[a.severity] ?? 3) - (order[b.severity] ?? 3);
  }) || [];

  // Counts for header summary chips
  const counts = sortedIssues.reduce((acc, i) => {
    acc[i.severity] = (acc[i.severity] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 200, padding: 20,
    }}>
      <div style={{
        background: '#0a1628', border: '1px solid #1e3a54', borderRadius: 12,
        width: '100%', maxWidth: 720, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 22px', borderBottom: '1px solid #162840',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#e8f4ff' }}>
              Shopbuilder Layout Review
            </div>
            <div style={{ fontSize: 11, color: '#4a7a9b', marginTop: 2 }}>
              Reviews clearances, safety, workflow, and code requirements
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: '#5a8aaa',
            fontSize: 20, cursor: 'pointer', padding: '0 6px',
          }}>×</button>
        </div>

        {/* Body — scrollable */}
        <div style={{ flex: 1, overflow: 'auto', padding: 22 }}>

          {/* Initial state — run button */}
          {!result && !loading && !error && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 13, color: '#c8dff0', marginBottom: 8 }}>
                Ready to analyze your layout
              </div>
              <div style={{ fontSize: 11, color: '#4a7a9b', marginBottom: 24, lineHeight: 1.6 }}>
                Shopbuilder will review {placed.filter(p => !p.isRoom).length} equipment items,
                {' '}{doors.length} doors, and {drains.length} floor drains.
                <br />
                Takes ~10 seconds.
              </div>
              <button onClick={runAnalysis} style={{
                background: 'linear-gradient(135deg,#003d80,#0066cc)',
                border: 'none', borderRadius: 8, color: '#fff',
                fontSize: 13, fontWeight: 600, padding: '11px 28px',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
                Run Analysis
              </button>
            </div>
          )}

          {/* Loading state with progress hints */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{
                width: 32, height: 32, margin: '0 auto 16px',
                border: '3px solid #162840', borderTop: '3px solid #3399ff',
                borderRadius: '50%', animation: 'sb_spin 1s linear infinite',
              }} />
              <div style={{ fontSize: 13, color: '#c8dff0', marginBottom: 6 }}>
                Analyzing layout...
              </div>
              <div style={{ fontSize: 10, color: '#4a7a9b' }}>
                Computing clearances · Checking code requirements · Reviewing workflow
              </div>
              <style>{`@keyframes sb_spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div style={{
              padding: 16, background: 'rgba(255,68,68,.08)',
              border: '1px solid #ff444444', borderRadius: 8,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#ff6644', marginBottom: 6 }}>
                Analysis Failed
              </div>
              <div style={{ fontSize: 11, color: '#c8a4a4', marginBottom: 12 }}>
                {error}
              </div>
              <button onClick={runAnalysis} style={{
                background: 'transparent', border: '1px solid #ff444466',
                borderRadius: 6, color: '#ff6644', fontSize: 11, fontWeight: 500,
                padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit',
              }}>
                Retry
              </button>
            </div>
          )}

          {/* Results */}
          {result && (
            <>
              {/* Summary banner */}
              <div style={{
                background: '#040b14', border: '1px solid #162840',
                borderRadius: 8, padding: 14, marginBottom: 16,
              }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#3399ff', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                  Summary
                </div>
                <div style={{ fontSize: 12, color: '#e8f4ff', lineHeight: 1.6, marginBottom: 10 }}>
                  {result.summary}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['critical', 'warning', 'info'].map(sev => counts[sev] && (
                    <span key={sev} style={{
                      fontSize: 10, fontWeight: 600,
                      color: sevStyles[sev].color,
                      background: sevStyles[sev].bg,
                      border: `1px solid ${sevStyles[sev].border}`,
                      borderRadius: 4, padding: '2px 8px',
                    }}>
                      {counts[sev]} {sevStyles[sev].label}
                    </span>
                  ))}
                  {sortedIssues.length === 0 && (
                    <span style={{
                      fontSize: 10, fontWeight: 600,
                      color: '#40c060', background: 'rgba(64,192,96,.1)',
                      border: '1px solid #40c06044',
                      borderRadius: 4, padding: '2px 8px',
                    }}>
                      NO ISSUES FOUND
                    </span>
                  )}
                </div>
              </div>

              {/* Issue cards */}
              {sortedIssues.map((issue, idx) => {
                const s = sevStyles[issue.severity] || sevStyles.info;
                return (
                  <div key={idx} style={{
                    background: s.bg, border: `1px solid ${s.border}`,
                    borderRadius: 8, padding: 12, marginBottom: 10,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: s.color }}>
                        {issue.title}
                      </div>
                      <span style={{
                        fontSize: 9, fontWeight: 700, color: s.color,
                        background: s.bg, border: `1px solid ${s.border}`,
                        borderRadius: 3, padding: '1px 6px',
                        letterSpacing: '.05em',
                      }}>
                        {s.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#c8dff0', lineHeight: 1.5, marginBottom: 6 }}>
                      {issue.description}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                      <span style={{ fontSize: 9, color: '#4a7a9b', fontWeight: 600 }}>
                        {issue.category}
                      </span>
                      {issue.affected?.length > 0 && (
                        <>
                          <span style={{ fontSize: 9, color: '#2a4a6a' }}>·</span>
                          <span style={{ fontSize: 9, color: '#6a9fc0' }}>
                            {issue.affected.join(', ')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Re-run button — useful if user adjusts layout and wants fresh analysis */}
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <button onClick={() => { setResult(null); runAnalysis(); }} style={{
                  background: 'transparent', border: '1px solid #1e3a54',
                  borderRadius: 6, color: '#6a9fc0',
                  fontSize: 11, fontWeight: 500, padding: '8px 16px',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  Re-run Analysis
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer disclaimer */}
        <div style={{
          padding: '10px 22px', borderTop: '1px solid #162840',
          fontSize: 9, color: '#2a4a6a', textAlign: 'center', lineHeight: 1.5,
        }}>
          Analysis is advisory only. Verify with manufacturer specs,
          local building codes, and on-site inspection before installation.
        </div>
      </div>
    </div>
  );
}
